import { IOption } from 'src/components/ui-kit/ReactSelect';
import { IPageData, IReqPage } from './pageTypes';
import { PersonBase, PersonComplete, Transfer } from './personTypes';
import { IOrder, ISearchCondition } from './searchConditionType';

/** 專利查詢參數 */
export interface IReqPatentRights {
  /** 專利類別。 */
  applyClass?: string;

  /** 權利是否存續。 */
  isEffective?: string;

  /** 搜尋號相關項目及值 */
  searchNo?: ISearchCondition;

  /** 公告日期起。 */
  publishBDate?: string;

  /** 公告日期迄。 */
  publishEDate?: string;

  /** 專利中英文名稱。 */
  patentName?: string;

  /** 搜尋人員名稱相關項目及值 */
  searchName?: ISearchCondition;

  /** 年費有效日期起。 */
  chargeExpirBDate?: string;

  /** 年費有效日期迄。*/
  chargeExpirEDate?: string;
}

export interface IReqPatentRightsPage extends IReqPatentRights, IReqPage, IOrder {}

/** 專利回傳結果 */
export interface IResPatentRights {
  /** 專利類別。 */
  sequence: string;

  /** 專利類別。 */
  applyClass: string;

  /** 證書號(專利權數號)。 */
  patentNo: string;

  /** 申請號。 */
  applNo: string;

  /** 專利名稱。 */
  patentName: string;

  /** 申請日期。 */
  applyDate: string;

  /** 發明人中文名稱。 */
  inventorNameC: string[];

  /** 專利權人中文名稱。 */
  applNameC: string[];

  /** 年費有效日期。*/
  chargeExpiredDate: string;
}

export type IResPatentRightsPage = IPageData<IResPatentRights>;

export interface IResPatentRightsDetail {
  /** 專利權狀態異動資料。 */
  patentRights: PatentRightsDetail;

  /** 專利案件年費繳納紀錄。 */
  patentAnnuity: PatentAnnuity;

  /** 專利優先權案。 */
  patentPriority: PatentPriority;

  /** 專利分割案。 */
  patentDivide: PatentDivide;

  /** 專利改請案。 */
  patentChange: PatentChange;

  /** 新型專利一案兩請關聯案件。 */
  twinsAnnounce: TwinsAnnounce;

  /** 發明專利一案兩請關聯案件。 */
  twinsApprove: TwinsApprove;

  /** 專利案件讓與紀錄。 */
  patentAlteration: PatentAlteration;

  /** 專利案件讓與紀錄。 */
  certPdfChkDetail: CertPdfChkDetail;

  /** QR code 圖片(base64編碼)。 */
  qrCodeImage: string;

  /** QR code uuid check api result code。 */
  qrCodeApiResultCode: string;
}

/**
 * 專利類別。
 */
export enum ApplyClass {
  /** 發明 */
  I = '發明',
  /** 新型 */
  M = '新型',
  /** 設計 */
  D = '設計',
}

/**
 * 專利是否存續。
 */
export enum ApplNow {
  All = '全部',
  Y = '是',
  N = '否',
}

/**
 * 專利搜尋項目(專利號)。
 */
export const PatentSearchNoOptions: IOption[] = [
  { value: 'patentno', label: '專利證書號數' },
  { value: 'noticeno', label: '公開號' },
  { value: 'publishno', label: '公告號' },
  { value: 'applno', label: '申請案號' },
];

/**
 * 專利搜尋項目(專利人)。
 */
export const PatentSearchNameOptions: IOption[] = [
  { value: 'applnamec', label: '專利權人中文名稱' },
  { value: 'applnamee', label: '專利權人英文名稱' },
  { value: 'inventornamec', label: '發明/新型創作人/設計人中文名稱' },
  { value: 'inventornamee', label: '發明/新型創作人/設計人英文名稱' },
  { value: 'agentnamec', label: '代理人中文名稱' },
];

/**
 * 專利搜尋結果排序選項。
 */
export const PatentOrderOptions: IOption[] = [
  { value: 'appl-no', label: '申請案號' },
  { value: 'appl-date', label: '申請日期' },
  { value: 'notice-no', label: '公開號' },
  { value: 'notice-date', label: '公開日期' },
  { value: 'publish-no', label: '公告號' },
  { value: 'publish-date', label: '公告日期' },
  { value: 'charge-expir-date', label: '年費有效日期' },
];

/** 專利權狀態異動資料。 */
export interface PatentRightsDetail {
  /** 公開號資訊。 */
  publicationReference: PublicationReference;

  /** 專利申請資訊。 */
  applicationReference: ApplicationReference;

  /** 專利名稱資訊。 */
  patentTitle: PatentTitle;

  /** 專利權資訊。 */
  patentRight: PatentRight;

  /** 申請人資訊。 */
  applicants: PersonComplete[];

  /** 發明人資訊。 */
  inventors: PersonBase[];

  /** 代理人資訊。 */
  agents: PersonComplete[];

  /** IPC分類資訊。 */
  ipcs: Ipc[];

  /** LOC分類資訊。 */
  locs: Loc[];

  /** 連結資訊。 */
  link: Links;
}

/** 公開號資訊。 */
interface PublicationReference {
  /** 公開號。 */
  noticeNo: string;

  /** 公開卷號。 */
  noticeVolumeNo: string;

  /** 公開期數。 */
  noticeIssueNo: string;

  /** 公開日期。 */
  noticeDate: string;

  /** 公告號。 */
  publishNo: string;

  /** 公告卷號。 */
  publishVolumeNo: string;

  /** 公告期數。 */
  publishIssueNo: string;

  /** 公告日期。 */
  publishDate: string;
}

/** 專利申請資訊。 */
interface ApplicationReference {
  /** 申請案號。 */
  applyNo: string;

  /** 申請日。 */
  applyDate: string;

  /** 設計種類描述。 */
  designTypeDesc: string;

  /** 專利類別。 */
  applyClass: string;

  /** 申請實體審查日。 */
  materialApplyDate: string;

  /** 實體審查註記。 */
  materialFlag: string;

  /** 初審審定日期。 */
  firstExamineDate: string;

  /** 再審申請日期。 */
  secondApplyDate: string;

  /** 再審審定日期。 */
  secondExamineDate: string;

  /** 一案兩請認可註記。 */
  agreeFlag: string;

  /** 聲明一案兩請註記。 */
  twinsFlag: string;
}

/** 專利名稱資訊。 */
interface PatentTitle {
  /** 專利中文名稱。 */
  patentNameChinese: string;

  /** 專利英文名稱。 */
  patentNameEnglish: string;
}

/** 專利權資訊。 */
interface PatentRight {
  /** 發證日期。 */
  licenceDate: string;

  /** 最新補發證日期。 */
  latestLicenceDate: string;

  /** 專利權數號。 */
  patentNo: string;

  /** 專利權始日。 */
  patentStartDate: string;

  /** 專利權止日。 */
  patentEndDate: string;

  /** 年費有效日期。 */
  chargeExpiredDate: string;

  /** 年費有效年次。 */
  chargeExpiredYear: string;

  /** 授權註記。 */
  rentStatus: number;

  /** 質權註記。 */
  mortgageStatus: number;

  /** 讓與註記。 */
  transferStatus: number;

  /** 繼承註記。 */
  inheritStatus: number;

  /** 信託註記。 */
  trustStatus: number;

  /** 異議註記。 */
  oppositionStatus: number;

  /** 舉發註記。 */
  nullityStatus: number;

  /** 延長註記。 */
  continueStatus: number;

  /** 延展註記。 */
  expandStatus: number;

  /** 消滅日期。 */
  cancelDate: string;

  /** 消滅原因。 */
  cancelResult: string;

  /** 撤銷日期。 */
  revokeDate: string;

  /** 撤銷原因代碼。 */
  revokeCode: string;

  /** 撤銷原因。 */
  revokeResult: string;

  /** 相關申請類別。 */
  depPatentClass: string;

  /** 相關申請案號。 */
  depApplyNo: string;
}

/** IPC分類資訊。 */
interface Ipc {
  /** IPC分類號。 */
  ipcFull: string;
}

/** LOC分類資訊。 */
interface Loc {
  /** IPC分類號。 */
  locFull: string;
}

/** 連結資訊。 */
interface Links {
  /** 專利公告公報書目XML連結。 */
  patentIsuRegSpecXmlUrl: string;

  /** 專利說明書影像掃描檔連結。 */
  patentPubImgTiffUrl: string;

  /** 專利說明書xml連結。 */
  patentPubXmlUrl: string;

  /** 專利公告公報書目影像掃描檔連結。 */
  patentIsuRegSpecmTiffUrl: string;
}

/** 專利案件年費繳納紀錄。 */
export interface PatentAnnuity {
  /** 年費有效日期。 */
  chargeExpiredDate: string;

  /** 年費有效年次。 */
  chargeExpiredYear: string;
}

/** 專利優先權案。 */
export interface PatentPriority {
  priorities: Priority[];
}

/** 專利優先權案。 */
interface Priority {
  /** 序號。 */
  sequence: number;

  /** 優先權國家代碼。 */
  priorityCountryCode: string;

  /** 優先權國家名稱。 */
  priorityCountryName: string;

  /** 優先權案號。 */
  priorityApplyNo: string;

  /** 優先權日期。 */
  priorityDate: string;
}

/** 專利分割案。 */
export interface PatentDivide {
  /** 專利分割後資料。 */
  divides: Divide[];
}

interface Divide {
  newApplyNo: string;
}

/** 專利改請案。 */
export interface PatentChange {
  /** 原申請案號。 */
  applyNo: string;

  /** 專利類別。 */
  applyClass: string;

  /** 改請後申請案號。 */
  newApplyNo: string;

  /** 相關申請案類別。 */
  newApplyClass: string;
}

export interface TwinsAnnounce {
  /** 申請案號。 */
  applyNo: string;
}

export interface TwinsApprove {
  /** 消滅新型專利申請案號。 */
  applyNo: string;

  /** 發明專利申請案號。 */
  twinsApplyNo: string;
}

/** 專利案件讓與紀錄。 */
export interface PatentAlteration {
  /** 讓與紀錄。 */
  alterations: Alteration[];
}

interface Alteration {
  /** 序號。 */
  sequence: number;

  /** 讓與人。 */
  transferers: Transfer[];

  /** 受讓人。 */
  transferees: Transfer[];
}

/** 權證PDF證書查核明細 */
export interface CertPdfChkDetail {
  /** PDF檔是否存在 Y: 存在 N: 不存在。 */
  pdfFlag: string;
  /** PDF檔下載連結。 */
  pdfUrl: string;
}
